/* eslint-disable no-unneeded-ternary */
/* eslint-disable @typescript-eslint/no-floating-promises */

import React, { useEffect, useState } from 'react';
import { useContent, useInViewEffect } from '@oneaudi/feature-app-utils';
import { ThemeProvider } from '@oneaudi/unified-web-common';
import { AsyncStateHolder, InitialState } from './FeatureAppTypes';
import { Context, useLogger } from './context';
import { useTrackingManager } from './context/useTrackingManager';
import { Static } from './components/static';
import { Animated } from './components/animated';
import { createInitialState } from './utils/stateUtils';
import { Content, HeadlessContent } from './EditorContentTypes';
import { mapHeadlessContent } from './utils/mapHeadlessContent';
import { getThemeName } from '../alpha-helpers';

const SequenceScrollFeatureApp: React.FunctionComponent<InitialState> = (
  initalState: InitialState,
) => {
  const { animation, themeScroll } = initalState;

  const isFallback = animation.fallback.enabled;

  const trackingManager = useTrackingManager();
  useEffect(() => {
    trackingManager.loaded();
  }, []);

  useInViewEffect(() => {
    trackingManager.impression(isFallback);
  });

  return (
    <ThemeProvider theme={getThemeName(themeScroll)}>
      {isFallback ? <Static {...initalState} /> : <Animated {...initalState} />}
    </ThemeProvider>
  );
};
interface Props {
  readonly asyncStateHolder: AsyncStateHolder;
}

const AsyncFeatureApp: React.FunctionComponent<Props> = ({ asyncStateHolder }: Props) => {
  /**
   * when asyncStateHolder is an object it represents the serialized state coming from the server
   * ready to be used as the initial state
   */
  const [state, setState] = useState<InitialState | undefined>(
    typeof asyncStateHolder === 'object' ? asyncStateHolder : undefined,
  );

  const { vueFormatterService, localeService } = React.useContext(Context);

  const initialContent = useContent<Content | HeadlessContent>();
  if (!initialContent) return null;
  const content = mapHeadlessContent(initialContent);
  const logger = useLogger();

  useEffect(() => {
    let mounted = true;
    if (state && content) {
      createInitialState(content, vueFormatterService, localeService, logger).then(
        (initialState) => {
          if (mounted) setState(initialState);
        },
      );
    }

    return () => {
      mounted = false;
    };
  }, [initialContent]);

  useEffect(() => {
    /**
     * when asyncStateHolder is a function it means the state could not be properly serialized by the server and
     * it is not available on the client. In that case this effect will try to fetch the state as soon
     * as the component is mounted on the client.
     */
    if (typeof asyncStateHolder === 'function') {
      logger?.info('SSR did not serialize any state');
      asyncStateHolder().then(setState);
    } else {
      logger?.info('SSR serialized state: ', asyncStateHolder);
    }
  }, []);

  if (!state) {
    return null;
  }

  return <SequenceScrollFeatureApp {...state} />;
};

export default AsyncFeatureApp;
